import {
  WSFilterApiOptionsLoader,
  WSFilterOption
} from "@wingspanhq/fe-component-library";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useMemo, useState } from "react";
import { useQueryPayeeRows } from "../../../query/search/payee/queries/useQueryPayeeRows";
import { PayeeRowsQuery } from "../../../services/search";
import { selectorPayerPayeeEngagementName } from "../selectors/selectorPayerPayeeEngagementName";
import { selectorPayerPayeeEngagements } from "../selectors/selectorPayerPayeeEngagements";

/**
 * Custom hook for providing API-based payee engagement filter options
 * with pagination and search support.
 *
 * @returns WSFilterApiOptionsLoader configuration for WSFilters
 *
 * @example
 * const engagementOptions = usePayeeEngagementFilterOptions();
 *
 * const filters: WSFilter[] = [
 *   {
 *     title: "Engagement",
 *     name: "engagementId",
 *     type: "select",
 *     apiOptions: engagementOptions
 *   }
 * ];
 */
export function usePayeeEngagementFilterOptions(
  baseQuery?: PayeeRowsQuery
): WSFilterApiOptionsLoader {
  const [searchText, setSearchText] = useState("");

  const query = useQueryPayeeRows({
    ...baseQuery,
    filter: {
      ...baseQuery?.filter,
      searchString: searchText || undefined
    }
  });

  const options = useMemo(() => {
    const result: WSFilterOption<"checkbox">[] = [];

    (query.data || []).forEach(payee => {
      if (!payee.user) return;

      const payeeNames = wsName({
        user: payee.user,
        member: payee.member,
        payerOwnedData: payee.payerOwnedData
      });

      const payeeName = payeeNames.getResolvedName();

      const filteredEngagements = selectorPayerPayeeEngagements(
        payee.engagements,
        true
      );

      filteredEngagements.forEach((engagement, _index, all) => {
        const engagementName = selectorPayerPayeeEngagementName(
          engagement,
          false
        );

        result.push({
          name: engagement.payerPayeeEngagementId,
          label: [payeeName, engagementName].filter(Boolean).join(" • "),
          helperText: undefined
        });
      });
    });

    return result;
  }, [query.data]);

  return {
    data: options,
    isLoading: query.isInitialLoading,
    pagination: {
      hasNextPage: query.hasNextPage ?? false,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: query.fetchNextPage
    },
    search: {
      searchText,
      onSearchChange: setSearchText
    }
  };
}
